import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  MessageSquare, 
  ThumbsUp, 
  ThumbsDown, 
  Filter,
  Search,
  Calendar,
  TrendingUp,
  Users,
  Star
} from 'lucide-react';
import { useAdminFeedbacks } from '@/hooks/useAdminFeedbacks';
import AdminAvatarImage from './AdminAvatarImage';

const FeedbackManagement = () => {
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');
  const [sentimentFilter, setSentimentFilter] = useState<'all' | 'positive' | 'negative' | 'neutral'>('all');

  const { feedbacks, stats, loading, error } = useAdminFeedbacks();

  const getSentimentBadge = (score: number) => {
    if (score >= 7) return { label: 'Positivo', color: 'bg-green-500/20 text-green-400' };
    if (score >= 4) return { label: 'Neutro', color: 'bg-yellow-500/20 text-yellow-400' };
    return { label: 'Negativo', color: 'bg-red-500/20 text-red-400' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    const statusMatch = statusFilter === 'all' || feedback.status === statusFilter;
    const sentimentMatch = sentimentFilter === 'all' || 
      (sentimentFilter === 'positive' && feedback.sentiment_score >= 7) ||
      (sentimentFilter === 'neutral' && feedback.sentiment_score >= 4 && feedback.sentiment_score < 7) ||
      (sentimentFilter === 'negative' && feedback.sentiment_score < 4);
    
    return statusMatch && sentimentMatch;
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-6">
          {/* Loading Skeletons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="bg-black/20 backdrop-blur-sm border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-16" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card className="bg-black/20 backdrop-blur-sm border-white/10">
          <CardContent className="p-8">
            <div className="text-center">
              <h3 className="text-white text-lg font-semibold mb-2">Erro ao carregar feedbacks</h3>
              <p className="text-white/60 text-sm">{error.message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="space-y-6">
        {/* Header Statistics - Mobile-First Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-black/20 backdrop-blur-sm border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Total Feedbacks</p>
                  <p className="text-white text-xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-black/20 backdrop-blur-sm border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <ThumbsUp className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Positivos</p>
                  <p className="text-white text-xl font-bold">{stats.positive}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-black/20 backdrop-blur-sm border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <ThumbsDown className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Negativos</p>
                  <p className="text-white text-xl font-bold">{stats.negative}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-black/20 backdrop-blur-sm border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-luxury-gold/20 rounded-lg">
                  <Star className="h-5 w-5 text-luxury-gold" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Score Médio</p>
                  <p className="text-white text-xl font-bold">{stats.avgScore}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters - Mobile Optimized */}
        <Card className="bg-black/20 backdrop-blur-sm border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex flex-col sm:flex-row gap-2 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                  <input
                    type="text"
                    placeholder="Buscar feedbacks..."
                    className="w-full pl-10 pr-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-luxury-red/50"
                  />
                </div>
                <select 
                  value={sentimentFilter}
                  onChange={(e) => setSentimentFilter(e.target.value as any)}
                  className="px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:border-luxury-red/50"
                >
                  <option value="all">Todos os sentimentos</option>
                  <option value="positive">Positivos</option>
                  <option value="neutral">Neutros</option>
                  <option value="negative">Negativos</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedbacks List - Mobile Optimized */}
        <div className="space-y-4">
          {filteredFeedbacks.length === 0 ? (
            <Card className="bg-black/20 backdrop-blur-sm border-white/10">
              <CardContent className="p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-2">Nenhum feedback encontrado</h3>
                  <p className="text-white/60 text-sm">
                    {feedbacks.length === 0 ? 'Não há feedbacks no sistema ainda.' : 'Nenhum feedback corresponde aos filtros selecionados.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            filteredFeedbacks.map((feedback) => {
              const sentiment = getSentimentBadge(feedback.sentiment_score);
              
              return (
                <Card key={feedback.id} className="bg-black/30 backdrop-blur-sm border-white/10 hover:bg-black/40 transition-all">
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      {/* Header with user info and sentiment */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <AdminAvatarImage
                            src={feedback.user.main_photo_url}
                            fallback={feedback.user.username.charAt(0).toUpperCase()}
                            className="h-10 w-10 flex-shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <h3 className="text-white font-medium truncate">@{feedback.user.username}</h3>
                            <div className="flex items-center gap-2 text-sm text-white/60">
                              <Calendar className="h-3 w-3" />
                              {formatDate(feedback.created_at)}
                            </div>
                          </div>
                        </div>
                        <Badge className={`${sentiment.color} flex-shrink-0`}>
                          {sentiment.label} {feedback.sentiment_score}/10
                        </Badge>
                      </div>

                      {/* Feedback content */}
                      <div className="bg-black/20 rounded-lg p-3 border border-white/5">
                        <p className="text-white/90 text-sm leading-relaxed">
                          {feedback.content}
                        </p>
                      </div>

                      {/* Action buttons with improved contrast */}
                      <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <Button size="sm" variant="admin-outline" className="flex-1">
                          Responder
                        </Button>
                        <Button size="sm" variant="admin-outline" className="flex-1">
                          Marcar como Lido
                        </Button>
                        <Button size="sm" variant="admin-outline" className="flex-1">
                          Arquivar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Quick Actions with improved contrast */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="admin-primary" className="flex-1">
            <TrendingUp className="h-4 w-4 mr-2" />
            Relatório de Sentimentos
          </Button>
          <Button variant="admin-outline" className="flex-1">
            <Users className="h-4 w-4 mr-2" />
            Exportar Dados
          </Button>
          <Button variant="admin-outline" className="flex-1">
            <Filter className="h-4 w-4 mr-2" />
            Filtros Avançados
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagement;
